import { FormArray, FormControl, FormGroup } from '@angular/forms';
import Decimal from 'decimal.js';
import { IQuoteItemWithContextDecorator, ItemType } from './models';
import { Observable } from 'rxjs';
import { InjectionToken } from '@angular/core';
import { FormGroupIdDecorator } from '../../../core/forms';
import { InletEventSource } from '../../shared/interfaces';
import { MatrixItem } from '../../../controls/matrix-selector-control/matrix-selector-control.component';
import { QuoteType } from './enums';
import { MaintenanceContractLineItems } from '../../../../../types';
import { Delta } from 'quill/core';

export const QUOTE_SERVICE = new InjectionToken('QuoteService');
export const QUOTE_TYPE = new InjectionToken<QuoteType>('QuoteType');

export enum QuoteRowAction {
  DROP = 0,
  DELETE,
}

export enum InletType {
  INTO = 0,
  ABOVE,
  BELOW,
  NONE,
}

export enum QuoteItemRecStatus {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  DELETED = 'DELETED',
}

export enum PriceAdjustmentType {
  FIXED = 'fixed',
  PERCENTAGE = 'percentage',
}

export enum VisitIntervalUnit {
  WEEK = 'week',
  MONTH = 'month',
}

export interface IBaseQuote {
  quote_id?: string;
  total: Decimal;
  total_inc_markup: Decimal;

  getQuoteItemById(
    id: string,
    container?:
      | FormArray<FormGroupIdDecorator<IQuoteItemFormGroup>>
      | undefined,
  ): FormGroup<IQuoteItemFormGroup> | undefined;

  getQuoteItemAtLine(
    linenumber: number,
    contianer: FormArray<FormGroup<IQuoteItemFormGroup>> | undefined,
    match: Partial<IQuoteItem> | undefined,
    context: number[],
  ): IQuoteItemWithContextDecorator | undefined;

  getQuoteItemByInternalId(
    id: number,
    container?:
      | FormArray<FormGroupIdDecorator<IQuoteItemFormGroup>>
      | undefined,
  ): FormGroup<IQuoteItemFormGroup> | undefined;

  setLineNumber(lineNumber: number): void;
}

export interface IQuoteMeta {
  quote_ref: string;
  customer_id: string;
  description: string;
  paragraph: string;
  consultant: string;
  attention: string;
  salutation: string;
  dtm_quote: string | Date;
}

export interface IQuote {
  quote_id: string;
  quote_type: QuoteType;
  quote_meta: IQuoteMeta;
  quote_items: IQuoteItem[];
  quote_item_hierarchy: IQuoteItem[];
  price_adjustment: IPriceAdjustment[];
  price_updates: IPriceUpdate[];
}

export interface IMaintenanceQuote {
  quote_id: string;
  quote_type: QuoteType;
  quote_meta: IQuoteMeta;
  quote_note: Delta;
  contract_lines: IMaintenanceContractLineItem[];
  quote_items: IQuoteItem[];
  quote_item_hierarchy?: IQuoteItem[];
}

export interface IMaintenanceContractLineItem {
  quote_item_id: string | null | undefined;
  item_line_number: number;
  line_item_name: string;
  line_item_type: string;
  line_item_description: Delta;
  price_matrix_day_rate: MatrixItem | null;
  day_rate: number;
  rate_tier_type: number | null;
  num_engineers: number;
  num_visits: number;
  visit_interval: number;
  visit_interval_unit: VisitIntervalUnit;
  visit_num_days: number;
}

export interface IQuoteLineItem {
  item_part_type: string;
  unit_price: number;
  quantity: number;
  parts?: Partial<IQuoteLineItem>[];
}

export interface IQuoteItem {
  id?: number;
  kind: string;
  quote_item_id?: string;
  quote_item_line_number: number;
  quote_item_part_number: number;
  item_line_number: number;
  item_part_number: number;
  item_part_type: string;
  item_part_id?: string;
  quote_item_part_id?: string;
  item_composite_id?: string;
  description?: string;
  quote_item_description?: string;
  quantity?: number;
  price?: number;
  unit_price?: number;
  markup?: number;
  parts?: Partial<IQuoteItem>[];
  part_group_ref?: string;
  part_is_enclosure?: boolean;
  configurations?: any;
  selectedMarkup?: number;
  flg_price_override?: boolean;
}

//@TODO: these interfaces will be useful
//       once I move the calculation logic
//       to respective objects
export interface IPhysicalAggregateQuoteItem extends Omit<IQuoteItem, 'parts'> {
  parts: Partial<IQuoteItem>[];
}

export interface ISumOfPartsAggregateQuoteItem
  extends Omit<IQuoteItem, 'parts'> {
  parts: Partial<IQuoteItem>[];
}

export interface IQuoteItemWithId {
  internal_id: number;
  item_type: ItemType;
}

export interface IHotJobState {
  hot_job_state: number;
  quote_id: string;
}

export interface IConfiguratorPart {
  id: string;
  type: ItemType;
  description: string;
  part_group_ref: string;
  part_price: number;
  quantity: number;
  ref: string;
  markup: number;
  markups?: number[];
  parts?: IConfiguratorPart[];
  configurations?: any;
  part_is_enclosure: boolean;
  selectedMarkup?: number;
}

export interface IConfiguratorAggContextLeaseRequest {
  internal_id: number;
  aggregate_type: string;
  title?: string;
  parent_id?: number;
  relinquish?: boolean;
  subscription?: Observable<any>;
}

export interface IQuoteListView {
  quote_id: string;
  quote_ref: string;
  customer_name: string;
  description: string;
  hot_job: number;
  dtm_quote: Date;
  summary: IQuoteSummary[];
  created_by: string;
  first_name: string;
  surname: string;
  quote_type: QuoteType;
  quote_status: string;
  item_total: number;
  service_total: number;
}

export interface IQuoteSummary {
  quote_id: string;
  part_group_ref: string;
  parts: IQuoteSummaryPart[];
  total: number;
  totalincmarkup: number;
}

export interface IQuoteDocumentsByCategory {
  type_name: string;
  documents: IQuoteDocumentMetadata[];
}

export interface IQuoteDocumentMetadata {
  quote_document_id: string;
  name: string;
}

export interface IQuoteSummaryPart {
  part_id: string;
  part_ref: string;
  quantity: number;
  unit_price: number;
  markup: number;
  total: number;
  totalincmarkup: number;
}

export interface IQuoteConfiguration {
  quote_id: string;
  configuration: IQuoteItemConfiguration[];
}

export interface IQuoteItemConfiguration {
  quote_item_configuration_sequence_id: string;
  quote_item_id: string;
  configuration_item_id: string;
  quote_item_quantity: number;
}

export interface IQuoteItemRowEvent {
  internal_id: number;
  context_id: number | null;
  line_number: number;
  lineItem: FormGroup<IQuoteItemFormGroup> | null;
  lineItemContext: FormGroupIdDecorator<IQuoteItemFormGroup> | null;
  hierarchy: number[];
}

export interface IQuoteItemRowToggleEvent extends IQuoteItemRowEvent {
  checked: boolean;
}

export interface IQuoteItemRowInletEvent extends IQuoteItemRowToggleEvent {
  direction: InletType;
  source: InletEventSource;
}

// source and target are line numbers
export interface IQuoteItemRowChange {
  action: QuoteRowAction;
  internalId?: number;
  activeInlet: number;
  sourceLineNumber: number;
  targetLineNumber?: number;
}

export interface HasQuoteId {
  quote_id: string;
}

export interface IQuoteItemRecordStatus {
  quote_item_id: string;
  record_status: QuoteItemRecStatus;
}

export interface IQuoteFormGroup {
  quote_id: FormControl<string | null>;
  quote_type: FormControl<QuoteType>;
  quote_meta: FormGroup<IQuoteMetaFormGroup>;
  quote_items: FormArray<FormGroup<IQuoteItemFormGroup>>;
  price_adjustment: FormArray<FormGroup<IQuotePriceAdjustmentFormGroup>>;
}

export interface IMaintenanceQuoteFormGroup {
  quote_id: FormControl<string>;
  quote_type: FormControl<QuoteType>;
  quote_meta: FormGroup<IQuoteMetaFormGroup>;
  contract_lines: MaintenanceContractLineItems;
  quote_items: FormArray<FormGroup<IQuoteItemFormGroup>>;
  quote_note: FormControl<Delta | null>;
}

export interface IQuoteMetaFormGroup {
  quote_ref: FormControl<string | null>;
  description: FormControl<string | null>;
  paragraph: FormControl<string | null>;
  customer_id: FormControl<string | null>;
  consultant: FormControl<string | null>;
  attention: FormControl<string | null>;
  salutation: FormControl<string | null>;
  dtm_quote: FormControl<Date | string | null>;
}

export interface IQuoteItemFormGroup {
  quote_item_id: FormControl<string>;
  kind: FormControl<string>;
  item_part_type: FormControl<string>;
  item_part_id?: FormControl<string>;
  item_composite_id?: FormControl<string>;
  quote_item_description: FormControl<string>;
  quantity: FormControl<number>;
  unit_price?: FormControl<number>;
  markup?: FormControl<number>;
  parts?: FormArray<FormGroup<IQuoteItemFormGroup>>;
  item_line_number: FormControl<number>;
  item_part_number: FormControl<number>;
  configurations: FormArray<FormGroup<any>>;
  flg_price_override?: FormControl<boolean>;
}

export interface IMaintenanceContractLineItemFormGroup {
  price_matrix_day_rate: FormControl<MatrixItem | null>;
  quote_item_id: FormControl<string | null | undefined>;
  item_line_number: FormControl<number>;
  line_item_type: FormControl<string | null>;
  line_item_name: FormControl<string>;
  line_item_description: FormControl<Delta>;
  day_rate: FormControl<number>;
  rate_tier_type: FormControl<number | null>;
  num_engineers: FormControl<number>;
  num_visits: FormControl<number>;
  visit_interval: FormControl<number>;
  visit_interval_unit: FormControl<VisitIntervalUnit>;
  visit_num_days: FormControl<number>;
}

export interface IQuotePriceAdjustmentFormGroup {
  adjustment_code: FormControl<string>;
  adjustment_type: FormControl<PriceAdjustmentType>;
  adjustment_value: FormControl<number | null>;
}

export interface IPriceAdjustment {
  adjustment_id?: number;
  adjustment_code: string;
  adjustment_type: PriceAdjustmentType;
  adjustment_value: number;
}

export interface IPriceUpdate {
  quote_item_id: string;
  item_line_number: number;
  item_part_id: string;
  part_ref: string;
  unit_price: number;
  new_price: number;
}

export interface PriceAdjustmentTypeValueChangeEvent {
  priceAdjustmentType: PriceAdjustmentType;
}
